import { isEmpty } from "lodash";
import { CIF_CUPS_BLOCKS_PER_LOAD } from "../../../core/constants/constants";
import {
  GetCifCupsResponse,
  ListCifCupsFormatted,
  CifCupsState,
  ManagementUpdateCup,
} from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";

const formatCifCups = (
  listCupsCif: GetCifCupsResponse[],
  ownerDataCups: OwnerDataCups[]
): ListCifCupsFormatted[] =>
  listCupsCif.map(
    ({ Identidad, DenominacionCliente, DirecionFact, cupGestion }) => ({
      id: Identidad,
      cif: Identidad,
      customerDescription: DenominacionCliente,
      fiscalAddress: DirecionFact,
      cups: cupGestion.map(
        ({
          CUPS,
          DireccionSuministro,
          IdCups,
          IdContrato,
          CodigoContrato,
          ContratoSituacion,
          FechaAlta,
          Entorno,
        }) => ({
          cupsCode: CUPS,
          supplyAddress: DireccionSuministro,
          cupsId: IdCups,
          cupsContractId: IdContrato,
          cupsAssociatedCif: Identidad,
          situation: ContratoSituacion,
          date: FechaAlta,
          envirorment:
            Entorno && !isEmpty(Entorno)
              ? Entorno
              : ownerDataCups.find(
                  ({ idCups }) => idCups.toString() === IdCups.toString()
                )?.envirorment || "Luz",
          cupsContractCode: CodigoContrato,
        })
      ),
    })
  );

const initialCifCupsState: CifCupsState = {
  listCupsCifRaw: [],
  listCupsCifTotalFiltered: [],
  listCupsCifFiltered: [],
  totalPages: 1,
  page: 1,
  searchedValue: "",
  isLoading: false,
};

const clearFirstLoadCifCups = (
  listCupsCifRaw: ListCifCupsFormatted[],
  listFiltered: ListCifCupsFormatted[] | null = null,
  searchedValue: string = ""
): CifCupsState => {
  const listCupsCifTotalFiltered = listFiltered ? listFiltered : listCupsCifRaw;
  return {
    listCupsCifRaw: listCupsCifRaw,
    listCupsCifTotalFiltered,
    listCupsCifFiltered: listCupsCifTotalFiltered.slice(
      0,
      CIF_CUPS_BLOCKS_PER_LOAD
    ),
    totalPages: Math.ceil(
      listCupsCifTotalFiltered.length / CIF_CUPS_BLOCKS_PER_LOAD
    ),
    page: 1,
    searchedValue: searchedValue,
    isLoading: false,
  };
};

const newLoadCipCups = (
  prevCifCupsState: CifCupsState,
  isLoading: boolean = false
): CifCupsState => {
  const { listCupsCifFiltered, listCupsCifTotalFiltered, page } =
    prevCifCupsState;

  return {
    ...prevCifCupsState,
    isLoading,
    listCupsCifFiltered: [
      ...listCupsCifFiltered,
      ...listCupsCifTotalFiltered.slice(
        CIF_CUPS_BLOCKS_PER_LOAD * page,
        CIF_CUPS_BLOCKS_PER_LOAD * (page + 1)
      ),
    ],
    page: page + 1,
  };
};

const getSelectedCups = (
  selectedCups: ManagementUpdateCup[],
  listCupsCifFiltered: ListCifCupsFormatted[]
) => {
  const filteredSelectedCups = [...selectedCups];
  listCupsCifFiltered?.forEach(({ cups }) => {
    const cupsMatched = cups.filter(
      ({ cupsId: cupsIdFiltered, cupsContractId: cupsContractIdFiltered }) =>
        selectedCups.findIndex(
          ({ cupsId, cupsContractId }) =>
            cupsIdFiltered === cupsId &&
            cupsContractIdFiltered !== cupsContractId
        ) > -1
    );

    if (cupsMatched.length) {
      cupsMatched.forEach((object) => filteredSelectedCups.push(object));
    }
  });

  const groupedDataByCup = filteredSelectedCups.reduce(
    (newCups, selectedCup) => {
      const key = selectedCup.cupsCode;
      if (!newCups[key]) {
        newCups[key] = [];
      }
      newCups[key].push(selectedCup);
      return newCups;
    },
    {} as { [key: string]: ManagementUpdateCup[] }
  );

  const newSelectedCups: ManagementUpdateCup[][] = [];
  Object.values(groupedDataByCup).forEach((group) => {
    const filteredBySituation: ManagementUpdateCup[] = group.filter(
      ({ situation }) => situation === "Contrato"
    );

    if (filteredBySituation.length === 1) {
      newSelectedCups.push(filteredBySituation);
      return;
    }

    const filteredCups = !filteredBySituation.length
      ? group
      : filteredBySituation;
    const filteredByMaxDate = filteredCups.reduce((previous, current) =>
      new Date(previous.date) > new Date(current.date) ? previous : current
    );
    newSelectedCups.push([filteredByMaxDate]);
  });

  return newSelectedCups.flat();
};

export {
  formatCifCups,
  initialCifCupsState,
  clearFirstLoadCifCups,
  newLoadCipCups,
  getSelectedCups,
};
